import { forwardRef } from 'react';

import { Badge } from '@/components/ui/badge';
import type { ParsedBlock, ParsedMessage } from '@/features/votes/chat-message';
import { cn } from '@/lib/utils';

import BlockRenderer from './BlockRenderer';
import CopyableId from './CopyableId';
import ThoughtsField from './fields/ThoughtsField';

function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

type RenderItem =
  | { kind: 'block'; block: ParsedBlock }
  | { kind: 'thoughts'; signature?: string; thoughts: string[] };

// Merge adjacent thought_signature + thought blocks into one "Thoughts" unit
// so the signature renders just above the reasoning inside a single container.
function groupBlocks(blocks: ParsedBlock[]): RenderItem[] {
  const items: RenderItem[] = [];
  for (const block of blocks) {
    if (block.type === 'thought' || block.type === 'thought_signature') {
      let last = items[items.length - 1];
      if (!last || last.kind !== 'thoughts') {
        last = { kind: 'thoughts', thoughts: [] };
        items.push(last);
      }
      if (block.type === 'thought_signature') last.signature = block.value;
      else last.thoughts.push(block.value);
    } else {
      items.push({ kind: 'block', block });
    }
  }
  return items;
}

interface MessageRendererProps {
  message: ParsedMessage;
  // Badge text for the message this view is about (the voted one, the flagged one).
  // Undefined leaves the message unhighlighted.
  highlightLabel?: string;
  isCompactionTrigger?: boolean;
  // 'bubble' = user message (right-aligned, adaptive-width); 'plain' = agent
  // side, rendered as bare blocks under the shared agent border.
  variant: 'bubble' | 'plain';
}

const MessageRenderer = forwardRef<HTMLDivElement, MessageRendererProps>(
  ({ message, highlightLabel, isCompactionTrigger, variant }, ref) => {
    const items = groupBlocks(message.blocks);
    const blocks = (
      <div className="space-y-2">
        {items.map((item, i) =>
          item.kind === 'thoughts' ? (
            <ThoughtsField
              key={i}
              signature={item.signature}
              thoughts={item.thoughts}
            />
          ) : (
            <BlockRenderer key={i} block={item.block} role={message.role} />
          )
        )}
      </div>
    );

    if (variant === 'plain') {
      return (
        <div
          ref={ref}
          className={cn(
            highlightLabel &&
              'rounded-md bg-primary/5 px-2 py-1.5 ring-1 ring-primary/40'
          )}
        >
          {highlightLabel && (
            <Badge className="mb-1.5 bg-primary text-[11px] text-primary-foreground">
              {highlightLabel}
            </Badge>
          )}
          {blocks}
        </div>
      );
    }

    return (
      <div ref={ref} className="flex justify-end">
        <div
          className={cn(
            'max-w-[92%] rounded-lg bg-muted/40 px-3 py-2 sm:max-w-[80%]',
            highlightLabel && 'bg-primary/5 ring-1 ring-primary/40'
          )}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                User
              </span>
              {message.id && <CopyableId value={message.id} />}
              {highlightLabel && (
                <Badge className="bg-primary text-[11px] text-primary-foreground">
                  {highlightLabel}
                </Badge>
              )}
              {isCompactionTrigger && (
                <Badge className="bg-amber-100 text-[11px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  Compaction triggered
                </Badge>
              )}
            </div>
            {message.createdAt && (
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground/60">
                {formatTime(message.createdAt)}
              </span>
            )}
          </div>
          <div className="mt-1.5">{blocks}</div>
        </div>
      </div>
    );
  }
);

export default MessageRenderer;
